export interface User {
  id: string;
  name: string;
  email: string;
  credits: number;
  photoURL?: string;
  emailVerified?: boolean;
}

export interface ConsultationProduct {
  id: string;
  title: string;
  description: string;
  price: number; // Cost in credits
  features: string[];
  apiLevel: 'basic' | 'advanced' | 'premium';
}

export interface VehicleData {
  plate: string;
  make: string;
  model: string;
  year: string;
  color: string;
  chassis: string; // Masked
  renavam: string; // Masked
  status: 'Regular' | 'Roubo/Furto' | 'Alienação Fiduciária' | 'Baixado';
  fines: {
    amount: number;
    description: string;
    date: string;
  }[];
  ownerType: 'Pessoa Física' | 'Pessoa Jurídica';
  apiSource: string;
  // Advanced fields
  restrictions?: string[];
  debts?: {
    ipva: number;
    licensing: number;
    fines: number;
    total: number;
  };
  auctionHistory?: {
    date: string;
    location: string;
    reason: string;
  }[];
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
}

export interface CreditPackage {
  id: string;
  credits: number;
  price: number; // in cents BRL
  name: string;
  popular?: boolean;
}

export interface ConsultationResult {
  vehicleData: VehicleData;
  creditsUsed: number;
  remainingCredits: number;
}

// =====================================================
// Product-based Consultation Types
// =====================================================

export interface ConsultationProductType {
  id: string;
  name: string;
  description: string;
  creditCost: number;
  apiProvider: string;
  category: 'vehicle' | 'person' | 'company' | 'document';
  features: string[];
  active: boolean;
}

// Consulta Placa - Informações Básicas
export interface ConsultaPlacaBasicaResult {
  placa: string;
  dataConsulta: string;
  
  dadosVeiculo: {
    chassi: string;
    anoFabricacao: string;
    anoModelo: string;
    marca: string;
    modelo: string;
    cor: string;
    segmento: string;
    combustivel: string;
    procedencia: string;
    municipio: string;
    uf: string;
  };
  
  dadosTecnicos: {
    tipoVeiculo: string;
    subSegmento: string;
    numeroMotor: string;
    numeroCaixaCambio: string;
    potencia: string;
    cilindradas: string;
  };
  
  dadosCarga: {
    numeroEixos: string;
    capacidadeMaximaTracao: string;
    capacidadePassageiro: string;
  };
  
  apiSource: string;
}

export interface ConsultaPlacaBasicaResponse {
  result: ConsultaPlacaBasicaResult;
  creditsUsed: number;
  remainingCredits: number;
}

// =====================================================
// Solicitar Relatório / Histórico
// =====================================================

export type TipoConsultaRelatorio = 'bronze' | 'prata' | 'ouro' | 'diamante' | 'personalizada';

export interface SolicitarRelatorioResult {
  protocolo: string;
  linkResultado: string;
  placa: string;
  tipoConsulta: string;
  dataSolicitacao: string;
  apiSource: string;
}

export interface SolicitarRelatorioResponse {
  result: SolicitarRelatorioResult;
  creditsUsed: number;
  remainingCredits: number;
}

// Additional information options for personalized reports
export const INFORMACOES_ADICIONAIS_OPTIONS: Record<string, string> = {
  '1': 'Sinistros com Perda Total (PT)',
  '3': 'Registro de Ofertas em Leilões PRIME',
  '4': 'Histórico de Roubo e Furto',
  '5': 'Restrições Financeiras e Judiciais (RENAJUD)',
  '7': 'Verificação Base Estadual (DETRANS)',
  '8': 'Informações gerais do veículo',
  '12': 'Referência de Preço (Fipe/Precificador)',
  '13': 'Registro de Ofertas em Leilões Simples',
  '17': 'Infrações de Trânsito (RENAINF)',
  '18': 'Recall',
  '19': 'Guia de Imagens',
  '23': 'Ficha Técnica Comparativa',
  '24': 'Custo Médio de Manutenção',
  '25': 'Proprietário atual (parcial)',
};

export const TIPO_CONSULTA_INFO: Record<TipoConsultaRelatorio, { name: string; description: string }> = {
  bronze: { name: 'Bronze', description: 'Consulta básica com dados essenciais do veículo' },
  prata: { name: 'Prata', description: 'Inclui histórico de leilões e restrições básicas' },
  ouro: { name: 'Ouro', description: 'Relatório completo com histórico detalhado' },
  diamante: { name: 'Diamante', description: 'Relatório premium com todas as informações disponíveis' },
  personalizada: { name: 'Personalizada', description: 'Escolha as informações que deseja incluir' },
};

// =====================================================
// Consultar Protocolo - Status do Relatório
// =====================================================

export type SituacaoConsulta = 'em_processamento' | 'parcialmente_finalizada' | 'finalizada';

export interface ConsultarProtocoloResult {
  protocolo: string;
  situacao: SituacaoConsulta;
  urlPdf?: string;
  dadosCompletos?: unknown[];
  sobreOsDados?: string;
  apiSource: string;
}

export interface ConsultarProtocoloResponse {
  result: ConsultarProtocoloResult;
  consultationId: string;
  placa: string;
  tipoConsulta: TipoConsultaRelatorio;
  dataSolicitacao: unknown; // Firestore Timestamp
}

// =====================================================
// Relatório no Histórico
// =====================================================

export interface RelatorioHistoricoItem {
  id: string;
  placa: string;
  protocolo: string;
  tipoConsulta: TipoConsultaRelatorio;
  situacao: SituacaoConsulta;
  urlPdf?: string;
  createdAt: unknown; // Firestore Timestamp
  creditsUsed: number;
}

export const SITUACAO_CONSULTA_INFO: Record<SituacaoConsulta, { label: string; color: string; icon: string }> = {
  em_processamento: { label: 'Em Processamento', color: 'yellow', icon: 'clock' },
  parcialmente_finalizada: { label: 'Parcialmente Finalizado', color: 'blue', icon: 'hourglass' },
  finalizada: { label: 'Concluído', color: 'green', icon: 'check-circle' },
};

// =====================================================
// Consulta RENAINF - Débitos por Infrações
// =====================================================

export interface InfracaoRenainfNormalizada {
  descricao: string;
  numeroAuto: string;
  valor: number;
  valorFormatado: string;
  orgaoAutuador: string;
  tipoAutoInfracao: string;
  localInfracao: string;
  municipio: string;
  // Aplicação (velocidade, etc)
  unidadeMedida?: string;
  limitePermitido?: string;
  medicaoReal?: string;
  // Eventos
  dataHoraInfracao?: string;
  dataCadastramento?: string;
  dataNotificacao?: string;
  dataEmissaoPenalidade?: string;
}

export interface ConsultarRenainfResult {
  placa: string;
  dataConsulta: string;
  possuiInfracoes: boolean;
  totalInfracoes: number;
  valorTotal: number;
  infracoes: InfracaoRenainfNormalizada[];
  apiSource: string;
}

export interface ConsultarRenainfResponse {
  result: ConsultarRenainfResult;
  creditsUsed: number;
  remainingCredits: number;
}

// =====================================================
// Consulta Preço FIPE
// =====================================================

export interface HistoricoPrecoFipe {
  mes: string;
  mesFormatado: string;
  preco: number;
  precoFormatado: string;
}

export interface VersaoFipeNormalizada {
  codigoFipe: string;
  modeloVersao: string;
  precoAtual: number;
  precoAtualFormatado: string;
  mesReferencia: string;
  historico: HistoricoPrecoFipe[];
  variacao12Meses: number;
  tendencia: 'alta' | 'baixa' | 'estavel';
}

export interface ConsultarPrecoFipeResult {
  placa: string;
  dataConsulta: string;
  dadosVeiculo: {
    chassi: string;
    anoFabricacao: string;
    anoModelo: string;
    marca: string;
    modelo: string;
    cor: string;
    combustivel: string;
  };
  versoes: VersaoFipeNormalizada[];
  apiSource: string;
}

export interface ConsultarPrecoFipeResponse {
  result: ConsultarPrecoFipeResult;
  creditsUsed: number;
  remainingCredits: number;
}

// =====================================================
// Consulta Proprietário Atual
// =====================================================

export type TipoDocumentoProprietario = 'FISICA' | 'JURIDICA';

export interface ConsultarProprietarioResult {
  placa: string;
  dataConsulta: string;
  proprietario: {
    nome: string;
    documentoMascarado: string;
    tipoDocumento: TipoDocumentoProprietario;
    isPessoaFisica: boolean;
  };
  apiSource: string;
}

export interface ConsultarProprietarioResponse {
  result: ConsultarProprietarioResult;
  creditsUsed: number;
  remainingCredits: number;
}

// =====================================================
// Consulta Leilão Prime
// =====================================================

export type ClassificacaoLeilao = 'A' | 'B' | 'C' | 'D' | 'N' | '';
export type StatusRegistro = 'sim' | 'nao' | 'indisponivel';
export type ParecerTecnico = 'favoravel' | 'desfavoravel' | 'alerta';
export type SituacaoAnaliseIA = 'concluido' | 'indisponivel' | 'processando';

export interface ClassificacaoLeilaoInfo {
  titulo: string;
  descricao: string;
}

export interface RegistroLeilaoNormalizado {
  comitente: string;
  lote: string;
  dataLeilao: string;
  placa: string;
  chassi: string;
  renavam: string;
  anoFabricacao: string;
  anoModelo: string;
  segmento: string;
  subSegmento: string;
  numeroMotor: string;
}

export interface RegistroRemarketingNormalizado {
  item: string;
  organizador: string;
  dataEvento: string;
  condicaoGeralVeiculo: string;
  condicaoMotor: string;
  condicaoCambio: string;
}

export interface DanoIANormalizado {
  local: string;
  descricao: string;
  probabilidade: number;
  severidade: 'baixa' | 'media' | 'alta';
}

export interface PecaDanificadaIANormalizada {
  descricao: string;
  probabilidade: number;
}

export interface ParecerTecnicoNormalizado {
  parecer: ParecerTecnico;
  parecerLabel: string;
  detalhes: {
    vistoriasNegadas: { status: StatusRegistro; label: string };
    frotaLocadora: { status: StatusRegistro; label: string };
    indiciosAcidentes: { status: StatusRegistro; label: string };
    veiculoImportado: { status: StatusRegistro; label: string };
  };
}

export interface ConsultarLeilaoPrimeResult {
  placa: string;
  dataConsulta: string;
  
  // Leilão
  possuiRegistroLeilao: boolean;
  statusLeilao: StatusRegistro;
  classificacao: ClassificacaoLeilao;
  classificacaoInfo: ClassificacaoLeilaoInfo | null;
  dicionarioClassificacoes: Record<string, ClassificacaoLeilaoInfo>;
  registrosLeilao: RegistroLeilaoNormalizado[];
  
  // Sinistros
  possuiSinistro: boolean;
  statusSinistro: StatusRegistro;
  
  // Parecer Técnico
  parecerTecnico: ParecerTecnicoNormalizado;
  
  // Remarketing
  possuiRemarketing: boolean;
  statusRemarketing: StatusRegistro;
  registrosRemarketing: RegistroRemarketingNormalizado[];
  fotosRemarketing: string[];
  
  // IA Analysis
  analiseIA: {
    situacao: SituacaoAnaliseIA;
    danos: DanoIANormalizado[];
    pecasDanificadas: PecaDanificadaIANormalizada[];
    imagens: string[];
  };
  
  apiSource: string;
}

export interface ConsultarLeilaoPrimeResponse {
  result: ConsultarLeilaoPrimeResult;
  creditsUsed: number;
  remainingCredits: number;
}

// Classificação colors and icons helper
export const CLASSIFICACAO_INFO: Record<string, { color: string; bgColor: string; borderColor: string; label: string }> = {
  A: { color: 'text-green-400', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/30', label: 'Sem Danos' },
  B: { color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', borderColor: 'border-yellow-500/30', label: 'Danos Leves' },
  C: { color: 'text-orange-400', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/30', label: 'Danos Médios' },
  D: { color: 'text-red-400', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30', label: 'Danos Graves' },
  N: { color: 'text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-500/30', label: 'Não Classificado' },
  '': { color: 'text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-500/30', label: 'Sem Registro' },
};

export const PARECER_INFO: Record<string, { color: string; bgColor: string; borderColor: string; icon: string }> = {
  favoravel: { color: 'text-green-400', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/30', icon: 'CheckCircle' },
  desfavoravel: { color: 'text-red-400', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30', icon: 'XCircle' },
  alerta: { color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30', icon: 'AlertTriangle' },
};

// =====================================================
// Consulta Sinistro com Perda Total
// =====================================================

export interface ConsultarSinistroPTResult {
  placa: string;
  dataConsulta: string;
  possuiSinistro: boolean;
  status: StatusRegistro;
  registro: string;
  apiSource: string;
}

export interface ConsultarSinistroPTResponse {
  result: ConsultarSinistroPTResult;
  creditsUsed: number;
  remainingCredits: number;
}
